<template>
  <div>
    <PForm
      title="Ajouter une nouvelle version"
      update-title="Modifier une version"
      activator-label="Ajouter une nouvelle version"
      size="lg"
      :submit-fn="submit"
      :updating="updating"
      @update:updating="$emit('update:updating', $event)"
      @open="onOpen"
      @reset="reset"
    >
      <template #activator="{ open, label }">
        <PButton v-if="authStore.ability.can('create', 'VersionAnnouncement')" size="lg" @click="open">{{
          label
        }}</PButton>
        <!-- btn won't hide otherwise because it fallbacks to slot's default content -->
        <div v-else></div>
      </template>
      <div class="flex flex-col gap-2">
        <div class="flex gap-2">
          <PInput label="Numéro" v-model="number" :error-messages="numberErrors" class="grow" />
          <PInput label="Date" v-model="releaseDate" :error-messages="releaseDateErrors" class="grow" />
        </div>
        <PTextarea label="Contenu" v-model="body" :error-messages="bodyErrors" />
      </div>
    </PForm>
  </div>
</template>

<script setup lang="ts">
import { PForm, PButton, PInput, PTextarea, usePastaValidation, pickModified, type Nullable } from "@pasta/box-v2";
import { type VersionAnnouncementDto } from "./version-announcement.dto";
import { useVersionAnnouncementStore } from "./version-announcement.store";
import { required } from "@vuelidate/validators";
import { useAuthStore } from "@pasta/front-auth-v2";

interface Props {
  updating?: Nullable<VersionAnnouncementDto>;
}
const props = withDefaults(defineProps<Props>(), { updating: null });
const emit = defineEmits<{
  (e: "refresh"): void;
  (e: "update:updating", value: Nullable<VersionAnnouncementDto>): void;
}>();

const authStore = useAuthStore();

const versionStore = useVersionAnnouncementStore();
const $validation = usePastaValidation();

const [number, numberErrors] = $validation.ref<string>("", [{ validator: required, message: "Ce champ est requis" }]);
const [releaseDate, releaseDateErrors] = $validation.ref<string>("", [
  { validator: required, message: "Ce champ est requis" },
]);
const [body, bodyErrors] = $validation.ref<string>("", [{ validator: required, message: "Ce champ est requis" }]);

async function submit() {
  const isValid = await $validation.validate();
  if (!isValid) return false;

  if (!props.updating) {
    if (
      !confirm(
        "Attention vous ne pourrez pas supprimer la version, êtes vous sûr(e) de vouloir ajouter cette version ?"
      )
    ) {
      return false;
    }

    await versionStore.create({
      number: number.value.trim(),
      releaseDate: releaseDate.value.trim(),
      body: body.value.trim().split("\n"),
    });
  } else {
    await versionStore.update(
      props.updating.id,
      pickModified(
        {
          number: number.value.trim(),
          releaseDate: releaseDate.value.trim(),
          body: body.value.trim().split("\n"),
        },
        props.updating
      )
    );
  }

  emit("refresh");
  return true;
}

async function onOpen() {
  if (props.updating) {
    number.value = props.updating.number;
    releaseDate.value = props.updating.releaseDate;
    body.value = props.updating.body.join("\n");
  }
}

async function reset() {
  number.value = "";
  releaseDate.value = "";
  body.value = "";
  $validation.reset();
}
</script>
